import React, {useEffect, useMemo, useState} from 'react'
import {Button, Form, Table} from 'react-bootstrap'
import {ToastContainer} from 'react-toastify'
import {useNavigate, useParams} from 'react-router-dom'
import {ponistiMojeZaduzenjeZaRolnu, vratiNalogeZaZaduzenuRolnu} from '../../../api/radniNalozi'
import {getRolnaDetalji} from '../../../api/rolneDetalji'
import {saveRepromaterijalPromet} from '../../../api/prometRepromaterijala'
import notifyToast from '../../../components/Toast/Toast'
import LoadingComponent from '../../../components/LoadingComponent/LoadingComponent'
import LokacijeRepromaterijalaDropdown from '../../../components/LokacijeRepromaterijalaDropdown/LokacijeRepromaterijalaDropdown'

const normalizeResponseData = (response: any) => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.data)) return response.data
    if (Array.isArray(response?.data?.data)) return response.data.data
    return []
}

const parseNumber = (value: any) => {
    if (typeof value === 'number') return value
    const normalized = String(value ?? '').trim().replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : 0
}

const getRadniNalogStavka = (item: any) =>
    item?.radni_nalog_stavka
    ?? item?.fmk_stavka?.radni_nalog_stavka
    ?? item?.fmk_stavka?.radni_nalog_stavk
    ?? item?.radni_nalog_stavk

const getStavkaId = (item: any) => {
    const radniNalogStavka = getRadniNalogStavka(item)
    return item?.IdStavke
        ?? item?.IdStavkeFMK
        ?? item?.Stavka
        ?? radniNalogStavka?.Id
        ?? radniNalogStavka?.id
        ?? item?.Id
        ?? item?.id
}

const getBrojNaloga = (item: any) => {
    const radniNalogStavka = getRadniNalogStavka(item)
    return radniNalogStavka?.BrojNaloga
        ?? radniNalogStavka?.brojNaloga
        ?? item?.BrojNaloga
        ?? item?.brojNaloga
        ?? '-'
}

const getSifraArtikla = (item: any) => {
    const radniNalogStavka = getRadniNalogStavka(item)
    return radniNalogStavka?.SifraArtikla
        ?? radniNalogStavka?.sifraArtikla
        ?? item?.SifraArtikla
        ?? item?.sifraArtikla
        ?? '-'
}

const getNazivArtikla = (item: any) => {
    const radniNalogStavka = getRadniNalogStavka(item)
    return radniNalogStavka?.NazivArtikla
        ?? radniNalogStavka?.nazivArtikla
        ?? item?.NazivArtikla
        ?? item?.nazivArtikla
        ?? '-'
}

const getOdjelId = (item: any) => {
    const radniNalogStavka = getRadniNalogStavka(item)
    return radniNalogStavka?.OdjelId ?? radniNalogStavka?.odjelId ?? item?.OdjelId ?? item?.odjelId
}

const getRowKey = (item: any, index: number) => String(item?.Id ?? item?.id ?? `${getStavkaId(item)}-${index}`)

const currentDate = () => new Date().toISOString().slice(0, 10)

const ZaduzeniMaterijalRolna: React.FC = () => {
    const {id} = useParams<{id: string}>()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [rolnaDetalji, setRolnaDetalji] = useState<any>()
    const [stavke, setStavke] = useState<any[]>([])
    const [krajnjaTezina, setKrajnjaTezina] = useState('')
    const [lokacija, setLokacija] = useState<string>('')
    const [napomena, setNapomena] = useState('')
    const [nalogSeRadiOdViseRolni, setNalogSeRadiOdViseRolni] = useState(false)
    const [stvarneKolicine, setStvarneKolicine] = useState<Record<string, string>>({})
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        const loadData = async () => {
            if (!id) return

            setLoading(true)
            try {
                const [detaljiResponse, naloziResponse] = await Promise.all([
                    getRolnaDetalji(Number(id)),
                    vratiNalogeZaZaduzenuRolnu({IdRolne: id}),
                ])

                setRolnaDetalji(detaljiResponse.data)
                setStavke(normalizeResponseData(naloziResponse))
            } catch (error: any) {
                notifyToast(false, error?.response?.data?.message ?? error.message ?? 'Greška pri učitavanju rolne.')
            } finally {
                setLoading(false)
            }
        }

        loadData()
    }, [id])

    const potrebnaKolicina = useMemo(() => {
        return stavke.reduce((sum, item) => sum + parseNumber(item?.PotrebnaKolicina), 0)
    }, [stavke])

    const trenutnaTezina = parseNumber(rolnaDetalji?.ReprMatTrenutnaKolicina)
    const krajnjaTezinaNumber = parseNumber(krajnjaTezina)
    const potrosenaKolicina = krajnjaTezina ? trenutnaTezina - krajnjaTezinaNumber : 0
    const odstupanje = potrebnaKolicina > 0 ? Math.abs(potrosenaKolicina - potrebnaKolicina) / potrebnaKolicina : 0
    const nemaPotrebneKolicinePoNormativu = stavke.length > 0 && potrebnaKolicina <= 0
    const prikaziUpozorenje = Boolean(krajnjaTezina) && potrebnaKolicina > 0 && odstupanje > 0.02
    const prikaziNapomenu = !nalogSeRadiOdViseRolni && (prikaziUpozorenje || nemaPotrebneKolicinePoNormativu)
    const komentarObavezan = prikaziNapomenu && !napomena.trim()

    useEffect(() => {
        if (!krajnjaTezina || !stavke.length || potrebnaKolicina <= 0) {
            setStvarneKolicine({})
            return
        }

        let allocated = 0
        const roundedPotrosenaKolicina = Math.round(potrosenaKolicina)
        const nextValues = stavke.reduce<Record<string, string>>((acc, item, index) => {
            const key = getRowKey(item, index)
            const potrebna = parseNumber(item?.PotrebnaKolicina)
            const isLast = index === stavke.length - 1
            const value = isLast
                ? roundedPotrosenaKolicina - allocated
                : Math.round((potrebna / potrebnaKolicina) * roundedPotrosenaKolicina)

            allocated += value
            acc[key] = String(value)
            return acc
        }, {})

        setStvarneKolicine(nextValues)
    }, [krajnjaTezina, potrebnaKolicina, potrosenaKolicina, stavke])

    const stavkeSaKolicinama = useMemo(() => {
        let runningKolicina = Math.round(trenutnaTezina)

        return stavke.map((item, index) => {
            const rowKey = getRowKey(item, index)
            const stvarnaKolicina = stvarneKolicine[rowKey] ?? ''
            const pocetnaKolicina = runningKolicina
            const krajnjaKolicina = stvarnaKolicina !== ''
                ? pocetnaKolicina - parseNumber(stvarnaKolicina)
                : ''

            if (krajnjaKolicina !== '') {
                runningKolicina = krajnjaKolicina
            }

            return {
                item,
                index,
                rowKey,
                pocetnaKolicina,
                stvarnaKolicina,
                krajnjaKolicina,
            }
        })
    }, [stavke, stvarneKolicine, trenutnaTezina])

    const handlePotvrdiPromet = async () => {
        if (!krajnjaTezina || !lokacija) {
            notifyToast(false, 'Unesite krajnju tezinu rolne i lokaciju.')
            return
        }

        if (!stavkeSaKolicinama.length) {
            notifyToast(false, 'Nema stavki za promet rolne.')
            return
        }

        if (komentarObavezan) {
            notifyToast(false, 'Unesite napomenu za odstupanje.')
            return
        }

        const invalidRow = stavkeSaKolicinama.find(({krajnjaKolicina, stvarnaKolicina, item}) => {
            return stvarnaKolicina === '' || krajnjaKolicina === '' || !getOdjelId(item)
        })

        if (invalidRow) {
            notifyToast(false, 'Provjerite utrosak materijala, krajnju kolicinu i odjel na svim stavkama.')
            return
        }

        setSaving(true)

        try {
            for (const {item, krajnjaKolicina} of stavkeSaKolicinama) {
                const payload = {
                    Datum: currentDate(),
                    Repromaterijal: id,
                    VrstaIzlaza: 2,
                    BrojDokumentaIzlaza: getBrojNaloga(item),
                    Krajnje: Math.round(Number(krajnjaKolicina)),
                    LokacijaRolne: lokacija,
                    Napomene: prikaziNapomenu ? napomena.trim() : null,
                    VanTolerancije: prikaziNapomenu ? 1 : 0,
                    KomentarNaToleranciju: prikaziNapomenu ? napomena.trim() : null,
                    Odjel: Number(getOdjelId(item)),
                    Poslovnica: 1,
                    Skenirano: Number(item?.Skenirano ?? item?.Skrenirano ?? 0),
                }

                await saveRepromaterijalPromet(payload)
            }

            await ponistiMojeZaduzenjeZaRolnu({IdRolne: id})

            notifyToast(true, 'Promet rolne uspjesno kreiran.')
            setTimeout(() => {
                navigate('/terminali/zaduzeni-materijal', {replace: true})
            }, 1500)
        } catch (error: any) {
            notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greska pri kreiranju prometa rolne.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className='container-xxl px-0'>
            <ToastContainer />

            {loading ? (
                <LoadingComponent />
            ) : (
                <>
                    <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 mb-4'>
                        <div className='min-w-0'>
                            <h1 className='fw-bolder mb-1 text-break'>
                                Promet rolne: <span className='text-success'>{id}</span>
                            </h1>
                            <div className='text-muted'>Unesi krajnju tezinu rolne i odaberi lokaciju.</div>
                        </div>

                        <Button variant='outline-secondary' className='px-4 w-100 w-md-auto' onClick={() => navigate(-1)}>
                            Nazad
                        </Button>
                    </div>

                    <div className='row g-3 mb-4'>
                        <div className='col-12 col-sm-6 col-xl-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body p-4'>
                                    <div className='text-muted small'>Dimenzije</div>
                                    <div className='fs-4 fw-bolder'>
                                        [{rolnaDetalji?.ReprMatDebljina || '-'} x {rolnaDetalji?.ReprMatSirina || '-'}]
                                    </div>
                                    <div className='text-muted small mt-2'>Rolna ID</div>
                                    <div className='fw-bolder text-primary text-break' style={{fontSize: '2.25rem', lineHeight: 1.1}}>
                                        R-{id}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='col-12 col-sm-6 col-xl-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body p-4'>
                                    <div className='text-muted small'>Boja</div>
                                    <div className='fs-4 fw-bolder text-danger'>{rolnaDetalji?.boja_lica?.BojaNaziv || '-'}</div>
                                    <div className='text-muted small mt-2'>Proizvodjac</div>
                                    <div className='fs-5 fw-bolder'>{rolnaDetalji?.proizvodjac?.PartnerNaziv || '-'}</div>
                                    <div className='text-muted small mt-2'>Kvalitet</div>
                                    <div className='fs-5 fw-bolder'>{rolnaDetalji?.kvalitet?.MatKvalitetNaziv || '-'}</div>
                                </div>
                            </div>
                        </div>

                        <div className='col-12 col-xl-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body p-4'>
                                    <div className='text-muted fs-4'>Trenutna tezina rolne</div>
                                    <div className='fw-semibold'>
                                        <span className='text-success fs-2'>{rolnaDetalji?.ReprMatTrenutnaKolicina || 0}</span> kg
                                    </div>

                                    <div className='text-muted fs-4 mt-3'>Potrebna kolicina po normativu</div>
                                    <div className='fw-semibold'>
                                        <span className='text-primary fs-2'>{potrebnaKolicina.toFixed(2)}</span> kg
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='card shadow-sm border-0 mb-4'>
                        <div className='card-body p-4'>
                            <div className='row g-3 align-items-end'>
                                <div className='col-12 col-lg-4'>
                                    <label className='form-label fw-semibold'>Krajnja tezina rolne</label>
                                    <Form.Control
                                        type='number'
                                        size='lg'
                                        value={krajnjaTezina}
                                        onChange={(e) => setKrajnjaTezina(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className='col-12 col-lg-4'>
                                    <label className='form-label fw-semibold'>Lokacija</label>
                                    <LokacijeRepromaterijalaDropdown
                                        value={lokacija}
                                        onChange={(name, value) => setLokacija(value)}
                                        selectName='Lokacija'
                                    />
                                </div>

                                <div className='col-12 col-lg-4'>
                                    {krajnjaTezina && lokacija && (
                                        <Button
                                            className='btn btn-success btn-lg w-100'
                                            disabled={saving || komentarObavezan}
                                            onClick={handlePotvrdiPromet}
                                        >
                                            {saving ? (
                                                <>
                                                    Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2' />
                                                </>
                                            ) : (
                                                <>
                                                    <i className='bi bi-check2-circle me-2' />
                                                    Potvrdi promet rolne
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>

                                <div className='col-12'>
                                    <label className='form-label fw-semibold d-block'>Nalog se radi od vise rolni</label>
                                    <div className='d-flex gap-3 flex-wrap'>
                                        <Form.Check
                                            type='radio'
                                            id='rolna_vise_rolni_ne'
                                            name='rolna_vise_rolni'
                                            label='Ne'
                                            checked={!nalogSeRadiOdViseRolni}
                                            onChange={() => setNalogSeRadiOdViseRolni(false)}
                                        />
                                        <Form.Check
                                            type='radio'
                                            id='rolna_vise_rolni_da'
                                            name='rolna_vise_rolni'
                                            label='Da'
                                            checked={nalogSeRadiOdViseRolni}
                                            onChange={() => setNalogSeRadiOdViseRolni(true)}
                                        />
                                    </div>
                                </div>

                                {krajnjaTezina && (
                                    <div className='col-12'>
                                        <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2'>
                                            <span className='badge badge-light-primary fs-6'>
                                                Potrosena kolicina: {potrosenaKolicina.toFixed(2)} kg
                                            </span>
                                            {!nalogSeRadiOdViseRolni && prikaziUpozorenje && (
                                                <span className='alert alert-danger py-2 px-3 mb-0 fw-bold'>
                                                    Molim provjerite krajnju kolicinu odstupa 2% od potrebne kolicine
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {!nalogSeRadiOdViseRolni && nemaPotrebneKolicinePoNormativu && (
                                    <div className='col-12'>
                                        <div className='alert alert-danger py-2 px-3 mb-0 fw-bold'>
                                            Nema potrebne kolicine po normativu. Unesite napomenu.
                                        </div>
                                    </div>
                                )}

                                {prikaziNapomenu && (
                                    <div className='col-12'>
                                        <label className='form-label required fw-semibold'>Napomena</label>
                                        <Form.Control
                                            as='textarea'
                                            rows={3}
                                            value={napomena}
                                            onChange={(e) => setNapomena(e.target.value)}
                                            placeholder='Unesite komentar za odstupanje'
                                            required
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='card shadow-sm border-0'>
                        <div className='card-body'>
                            <div className='d-flex align-items-center justify-content-between gap-2 mb-3'>
                                <h3 className='fw-bolder mb-0'>Odabrane stavke za materijal</h3>
                                <div className='text-muted small'>Ukupno stavki: {stavke.length}</div>
                            </div>

                            <div className='table-responsive'>
                                <Table hover className='align-middle mb-0'>
                                    <thead>
                                    <tr className='bg-primary text-white'>
                                        <th className='text-nowrap'>Broj naloga</th>
                                        <th className='text-nowrap'>Id radne stavke</th>
                                        <th className='text-nowrap'>Sifra artikla</th>
                                        <th className='text-nowrap'>Naziv artikla</th>
                                        <th className='text-nowrap'>Potrebna kolicina po normativu</th>
                                        <th className='text-nowrap'>Pocetna kolicina</th>
                                        <th className='text-nowrap'>Utrosak materijala</th>
                                        <th className='text-nowrap'>Krajnja kolicina</th>
                                        <th className='text-nowrap text-center'>Skenirano</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {stavkeSaKolicinama.map(({item, rowKey, pocetnaKolicina, stvarnaKolicina, krajnjaKolicina}) => {
                                        const stavkaId = getStavkaId(item)
                                        const brojNaloga = getBrojNaloga(item)
                                        const sifraArtikla = getSifraArtikla(item)
                                        const nazivArtikla = getNazivArtikla(item)
                                        const potrebna = item.PotrebnaKolicina ?? ''

                                        return (
                                            <tr key={rowKey}>
                                                <td className='fw-semibold text-nowrap'>{brojNaloga}</td>
                                                <td className='text-nowrap'>{stavkaId ?? '-'}</td>
                                                <td className='text-nowrap'>{sifraArtikla}</td>
                                                <td>{nazivArtikla}</td>
                                                <td>{potrebna || '-'}</td>
                                                <td className='fw-semibold text-nowrap'>{pocetnaKolicina}</td>
                                                <td style={{minWidth: 150}}>
                                                    <Form.Control
                                                        type='number'
                                                        value={stvarnaKolicina}
                                                        placeholder={String(potrebna)}
                                                        onChange={(e) => {
                                                            const value = e.target.value
                                                            setStvarneKolicine((prev) => ({...prev, [rowKey]: value}))
                                                        }}
                                                    />
                                                </td>
                                                <td className='fw-semibold text-nowrap'>
                                                    {typeof krajnjaKolicina === 'number' ? Math.round(krajnjaKolicina) : '-'}
                                                </td>
                                                <td className='text-center'>
                                                    {Number(item.Skenirano ?? item.Skrenirano) === 1 ? (
                                                        <span className='badge bg-success'>DA</span>
                                                    ) : (
                                                        <span className='badge bg-danger'>NE</span>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default ZaduzeniMaterijalRolna
